import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  adminLogin, adminMe, clearToken, getToken, formatApiError,
  timeGetTimer, timeStart, timePause, timeResume, timeStop, timeDiscard, timeHeartbeat,
  timeCreateEntry, timeListEntries, timeConfirmEntry, timeVoidEntry, timeLockEntry,
  timeGenerateSuggestions, timeListRuntime, timeReport, timeActivityTimeline, timeWeeklyDigest,
} from "@/lib/api";
import { Seo } from "@/components/Seo";

const CATEGORIES = ["BUILD_AND_PRODUCT","FOUNDER_REVIEW","STRATEGY","FINANCE_AND_ACCOUNTING","LEGAL_AND_COMPLIANCE","OPERATIONS","MARKETING_AND_BRAND","RESEARCH","MEETING_OR_CALL","BUSINESS_TRAVEL","ADMINISTRATION","SHARED_ECOSYSTEM","OTHER","UNKNOWN"];
const DIMENSIONS = ["JMH","NIRVA_LIFE","IMPOSSIBLE","NIRVACARE","GROWTH_JARVIS","SOCIAL_STUDIO","STEWARD_AND_STONE","STRATEGIC_ASSETS","JENNAE_MICHELLE_FOUNDATION","SHARED_ECOSYSTEM","UNKNOWN"];
// Founder-facing display only — canonical stored value stays GROWTH_JARVIS (compatibility); MAC is the executive orchestrator identity.
const dimLabel = (d) => (d === "GROWTH_JARVIS" ? "GROWTH_MAC" : d);
const IDLE_THRESHOLD_MIN = 10;

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const fmtHM = (min) => { const m = Math.max(0, Math.round(min || 0)); return `${Math.floor(m / 60)}h ${m % 60}m`; };
const todayISO = () => new Date().toISOString().slice(0, 10);
const weekAgoISO = () => new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10);
const monthAgoISO = () => new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10);

const inputCls = "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-3 py-2 text-[var(--bone)] font-serif2 text-base placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";
const labelCls = "font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-1 block";

function LoginView({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const u = await adminLogin(email.trim(), password); onAuthed(u); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Sign-in failed.")); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen glow-navy flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="time-login-form">
        <p className="text-center font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Jennae Michelle Holdings</p>
        <h1 className="mt-3 text-center font-serif2 text-3xl text-[var(--bone)]">Founder Time Ledger</h1>
        <p className="mt-3 text-center text-sm text-[var(--muted)]">Founder-only. Records your personal working time.</p>
        <div className="mt-10 flex flex-col gap-4">
          <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="time-login-email" />
          <input className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="time-login-password" />
          <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid="time-login-submit">{loading ? "Signing in…" : "Enter"}</button>
        </div>
      </form>
    </div>
  );
}

function TimerWidget({ onChange }) {
  const [timer, setTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [form, setForm] = useState({ entity_dimension: "JMH", activity_category: "BUILD_AND_PRODUCT", project: "", description: "" });
  const [idlePrompt, setIdlePrompt] = useState(false);
  const lastActivity = useRef(Date.now());
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const refresh = useCallback(async () => {
    try { const r = await timeGetTimer(); setTimer(r.active ? r.timer : null); setElapsed(r.active ? r.elapsed_minutes_live : 0); }
    catch (e) { /* auth handled upstream */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // live seconds ticker while running
  useEffect(() => {
    if (!timer || timer.state !== "RUNNING") return;
    const base = elapsed; const startTick = Date.now();
    const iv = setInterval(() => setElapsed(base + (Date.now() - startTick) / 60000), 1000 * 15);
    return () => clearInterval(iv);
  }, [timer?.state]); // eslint-disable-line

  // idle detection: any pointer/key resets; after threshold while RUNNING → prompt
  useEffect(() => {
    const mark = () => { lastActivity.current = Date.now(); };
    window.addEventListener("mousemove", mark); window.addEventListener("keydown", mark); window.addEventListener("click", mark);
    const iv = setInterval(async () => {
      if (timer?.state === "RUNNING") {
        const idleMin = (Date.now() - lastActivity.current) / 60000;
        if (idleMin >= IDLE_THRESHOLD_MIN) setIdlePrompt(true);
        else { try { await timeHeartbeat(new Date().toISOString()); } catch {} }
      }
    }, 60000);
    return () => { window.removeEventListener("mousemove", mark); window.removeEventListener("keydown", mark); window.removeEventListener("click", mark); clearInterval(iv); };
  }, [timer?.state]);

  const start = async () => {
    try { const r = await timeStart({ command_id: uid(), timezone: tz, idle_threshold_minutes: IDLE_THRESHOLD_MIN, ...form }); setTimer(r.timer); setElapsed(0); lastActivity.current = Date.now(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not start timer.")); }
  };
  const pause = async () => { try { const r = await timePause(uid()); setTimer(r.timer); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resume = async () => { try { const r = await timeResume(uid()); setTimer(r.timer); lastActivity.current = Date.now(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const stop = async (effective_ended_at = null, idle_reason = null) => {
    try { const r = await timeStop({ command_id: uid(), effective_ended_at, idle_reason }); setTimer(null); setElapsed(0); setIdlePrompt(false); onChange?.(); if (r.created) toast.success("Draft entry created — confirm it below."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Could not stop timer.")); }
  };
  const discard = async () => { try { await timeDiscard(uid()); setTimer(null); setElapsed(0); setIdlePrompt(false); onChange?.(); toast("Timer discarded — nothing recorded."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="corner-frame px-6 py-6" data-testid="timer-widget">
      <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80">Live Timer</p>
      {!timer ? (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="timer-idle">
          <div><label className={labelCls}>Entity / Workstream</label>
            <select className={inputCls} value={form.entity_dimension} onChange={(e) => setForm({ ...form, entity_dimension: e.target.value })} data-testid="timer-dimension">
              {DIMENSIONS.map((d) => <option key={d} value={d} className="bg-[#0a1420]">{dimLabel(d)}</option>)}
            </select></div>
          <div><label className={labelCls}>Activity Category</label>
            <select className={inputCls} value={form.activity_category} onChange={(e) => setForm({ ...form, activity_category: e.target.value })} data-testid="timer-category">
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0a1420]">{c}</option>)}
            </select></div>
          <div><label className={labelCls}>Project (optional)</label>
            <input className={inputCls} value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} placeholder="e.g., Close console" data-testid="timer-project" /></div>
          <div><label className={labelCls}>Description (optional)</label>
            <input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What are you working on?" data-testid="timer-description" /></div>
          <div className="md:col-span-2"><button className="jmh-cta w-full" onClick={start} data-testid="timer-start-btn">Start timer</button></div>
        </div>
      ) : (
        <div className="mt-4" data-testid="timer-running">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="font-serif2 text-5xl text-[var(--bone)]" data-testid="timer-elapsed">{fmtHM(elapsed)}</p>
              <p className="mt-1 text-xs text-[var(--muted)]">{timer.entity_dimension} · {timer.activity_category}{timer.project ? ` · ${timer.project}` : ""}</p>
              <span className={`mt-2 inline-block font-display uppercase tracking-wide2 text-[10px] px-2 py-0.5 border ${timer.state === "RUNNING" ? "border-[#6fae8f] text-[#8fcbac]" : "border-[rgba(180,139,63,0.5)] text-[var(--gold-lite)]"}`} data-testid="timer-state">{timer.state}</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {timer.state === "RUNNING" ? (
                <button className="border border-[rgba(180,139,63,0.4)] px-4 py-2 text-xs uppercase tracking-wide2 text-[var(--gold-lite)]" onClick={pause} data-testid="timer-pause-btn">Pause</button>
              ) : (
                <button className="border border-[rgba(180,139,63,0.4)] px-4 py-2 text-xs uppercase tracking-wide2 text-[var(--gold-lite)]" onClick={resume} data-testid="timer-resume-btn">Resume</button>
              )}
              <button className="jmh-cta px-4 py-2" onClick={() => stop()} data-testid="timer-stop-btn">Stop</button>
              <button className="border border-[rgba(217,99,59,0.5)] px-4 py-2 text-xs uppercase tracking-wide2 text-[#e88f6a]" onClick={discard} data-testid="timer-discard-btn">Discard</button>
            </div>
          </div>
          {idlePrompt && (
            <div className="mt-4 border border-[rgba(217,139,95,0.5)] px-4 py-3" data-testid="idle-prompt">
              <p className="text-sm text-[#e7a877]">You've been inactive for {IDLE_THRESHOLD_MIN}+ minutes. Are you still working?</p>
              <div className="mt-2 flex gap-2 flex-wrap">
                <button className="border border-[rgba(180,139,63,0.4)] px-3 py-1.5 text-xs uppercase tracking-wide2 text-[var(--gold-lite)]" onClick={() => { lastActivity.current = Date.now(); setIdlePrompt(false); }} data-testid="idle-still-working">Yes, keep going</button>
                <button className="border border-[rgba(217,139,95,0.5)] px-3 py-1.5 text-xs uppercase tracking-wide2 text-[#e88f6a]" onClick={() => stop(new Date(lastActivity.current).toISOString(), "confirmed_idle")} data-testid="idle-stop-at-active">Stop at last activity</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QuickEntry({ onCreated }) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const defaultStart = () => { const d = new Date(Date.now() - 60 * 60000); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
  const [f, setF] = useState({ work_date: todayISO(), start: defaultStart(), duration_minutes: 60, entity_dimension: "JMH", activity_category: "BUILD_AND_PRODUCT", description: "", confirm: true });
  const submit = async () => {
    try {
      const started_at = new Date(`${f.work_date}T${f.start}:00`).toISOString();
      await timeCreateEntry({ idempotency_key: uid(), started_at, duration_minutes: Number(f.duration_minutes), timezone: tz, entity_dimension: f.entity_dimension, activity_category: f.activity_category, description: f.description, confirm: f.confirm });
      toast.success("Entry saved."); onCreated?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "OVERLAPPING_CONFIRMED_ENTRY") toast.error("Overlaps an existing confirmed entry. Adjust the time.");
      else toast.error(formatApiError(d, "Could not save entry."));
    }
  };
  return (
    <div className="corner-frame px-6 py-6" data-testid="quick-entry">
      <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80">Quick / Backdated Entry</p>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        <div><label className={labelCls}>Date</label><input type="date" max={todayISO()} className={inputCls} value={f.work_date} onChange={(e) => setF({ ...f, work_date: e.target.value })} data-testid="qe-date" /></div>
        <div><label className={labelCls}>Start</label><input type="time" className={inputCls} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="qe-start" /></div>
        <div><label className={labelCls}>Minutes</label><input type="number" min="1" max="960" className={inputCls} value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} data-testid="qe-minutes" /></div>
        <div><label className={labelCls}>Entity</label><select className={inputCls} value={f.entity_dimension} onChange={(e) => setF({ ...f, entity_dimension: e.target.value })} data-testid="qe-dimension">{DIMENSIONS.map((d) => <option key={d} value={d} className="bg-[#0a1420]">{dimLabel(d)}</option>)}</select></div>
        <div><label className={labelCls}>Category</label><select className={inputCls} value={f.activity_category} onChange={(e) => setF({ ...f, activity_category: e.target.value })} data-testid="qe-category">{CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0a1420]">{c}</option>)}</select></div>
        <div><label className={labelCls}>Description</label><input className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="qe-description" /></div>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-[var(--muted)]"><input type="checkbox" checked={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.checked })} data-testid="qe-confirm-toggle" /> Confirm immediately</label>
      <button className="jmh-cta mt-3 w-full" onClick={submit} data-testid="qe-submit">Add entry</button>
    </div>
  );
}

const Metric = ({ label, value, testid, tone = "bone" }) => (
  <div className="border border-[rgba(180,139,63,0.18)] px-5 py-4" data-testid={testid}>
    <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--muted)]">{label}</p>
    <p className={`mt-2 font-serif2 text-3xl ${tone === "attention" ? "text-[#e7a877]" : "text-[var(--bone)]"}`}>{value}</p>
  </div>
);

function Breakdown({ title, obj, testid }) {
  const rows = Object.entries(obj || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  return (
    <div className="border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid={testid}>
      <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-2">{title}</p>
      {rows.length === 0 ? <p className="text-sm text-[var(--muted)]">No time recorded.</p> :
        rows.map(([k, v]) => <div key={k} className="flex items-center justify-between py-1.5 border-t border-[rgba(180,139,63,0.1)] first:border-t-0"><span className="text-sm text-[var(--muted)]">{k}</span><span className="text-sm text-[var(--bone)]">{fmtHM(v)}</span></div>)}
    </div>
  );
}

function Dashboard({ refreshKey }) {
  const [range, setRange] = useState("week");
  const [rep, setRep] = useState(null);
  const load = useCallback(async () => {
    const params = range === "today" ? { date_from: todayISO(), date_to: todayISO() }
      : range === "week" ? { date_from: weekAgoISO(), date_to: todayISO() }
      : range === "month" ? { date_from: monthAgoISO(), date_to: todayISO() } : {};
    try { setRep(await timeReport(params)); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [range]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!rep) return <div className="mt-6 text-sm text-[var(--muted)]" data-testid="dash-loading">Loading dashboard…</div>;
  const t = rep.founder_totals;
  return (
    <div className="mt-2" data-testid="dashboard">
      <div className="flex items-center gap-2 flex-wrap">
        {["today", "week", "month", "all"].map((r) => (
          <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-wide2 border ${range === r ? "border-[var(--gold)] text-[var(--gold-lite)]" : "border-[rgba(180,139,63,0.25)] text-[var(--muted)]"}`}>{r}</button>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Confirmed" value={fmtHM(t.confirmed_minutes)} testid="dash-confirmed" />
        <Metric label="Draft" value={fmtHM(t.draft_minutes)} testid="dash-draft" tone={t.draft_minutes ? "attention" : "bone"} />
        <Metric label="Suggested" value={fmtHM(t.suggested_minutes)} testid="dash-suggested" tone={t.suggested_minutes ? "attention" : "bone"} />
        <Metric label="Unallocated / Unknown" value={fmtHM(t.unallocated_minutes)} testid="dash-unallocated" tone={t.unallocated_minutes ? "attention" : "bone"} />
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Breakdown title="Hours by Entity / Workstream" obj={rep.by_entity_dimension} testid="dash-by-entity" />
        <Breakdown title="Hours by Activity Category" obj={rep.by_activity_category} testid="dash-by-category" />
      </div>
      <div className="mt-3 border border-dashed border-[rgba(180,139,63,0.3)] px-4 py-3" data-testid="dash-runtime">
        <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--muted)]">Agent Runtime — {rep.agent_runtime.classification}</p>
        <p className="mt-1 text-sm text-[var(--bone)]">{fmtHM(rep.agent_runtime.elapsed_runtime_minutes)} across {rep.agent_runtime.record_count} runs · <span className="text-[var(--muted)]">shown separately, never counted as Founder labor</span></p>
      </div>
      {t.needs_duration_evidence > 0 && (
        <p className="mt-3 text-sm text-[#e7a877]" data-testid="dash-needs-duration">⧗ {t.needs_duration_evidence} touchpoint suggestion{t.needs_duration_evidence === 1 ? "" : "s"} need duration evidence — time not measured, excluded from all totals.</p>
      )}
      {rep.overlap_warnings.length > 0 && (
        <p className="mt-3 text-sm text-[#e7a877]" data-testid="dash-overlaps">⚠ {rep.overlap_warnings.length} overlapping confirmed entr{rep.overlap_warnings.length === 1 ? "y" : "ies"} detected.</p>
      )}
      <p className="mt-3 text-[10px] uppercase tracking-wide2 text-[var(--muted)]">{rep.guardrail_note}</p>
    </div>
  );
}

function EntriesList({ refreshKey, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("review"); // review | confirmed | suggested | all
  const [limit, setLimit] = useState(50);
  const load = useCallback(async () => { try { setEntries(await timeListEntries()); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setLimit(50); }, [filter]);
  const act = async (fn, ...args) => { try { await fn(...args); onChanged?.(); load(); } catch (e) { const d = e.response?.data?.detail; if (d?.error === "OVERLAPPING_CONFIRMED_ENTRY") toast.error("Overlaps a confirmed entry."); else toast.error(formatApiError(d)); } };
  const stateColor = (s) => ({ CONFIRMED: "text-[#8fcbac] border-[#6fae8f]", DRAFT: "text-[var(--gold-lite)] border-[rgba(180,139,63,0.5)]", SUGGESTED: "text-[#e7a877] border-[#d98b5f]", LOCKED: "text-[var(--muted)] border-[rgba(180,139,63,0.3)]", SUPERSEDED: "text-[var(--muted)] border-[rgba(180,139,63,0.2)]", VOIDED: "text-[var(--muted)] border-[rgba(180,139,63,0.2)]" }[s] || "text-[var(--muted)] border-[rgba(180,139,63,0.2)]");
  const FILTERS = {
    review: ["DRAFT"], confirmed: ["CONFIRMED", "LOCKED"], suggested: ["SUGGESTED"],
    all: ["DRAFT", "SUGGESTED", "CONFIRMED", "LOCKED", "SUPERSEDED", "VOIDED"],
  };
  const counts = entries.reduce((a, e) => { a[e.entry_state] = (a[e.entry_state] || 0) + 1; return a; }, {});
  const filtered = entries.filter((e) => FILTERS[filter].includes(e.entry_state));
  const shown = filtered.slice(0, limit);
  const chip = (id, label, n) => (
    <button key={id} onClick={() => setFilter(id)} data-testid={`entries-filter-${id}`}
      className={`px-3 py-1.5 text-xs uppercase tracking-wide2 border ${filter === id ? "border-[var(--gold)] text-[var(--gold-lite)]" : "border-[rgba(180,139,63,0.25)] text-[var(--muted)]"}`}>{label}{n != null ? ` · ${n}` : ""}</button>
  );
  return (
    <div className="mt-2" data-testid="entries-list">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {chip("review", "Needs review", counts.DRAFT || 0)}
        {chip("suggested", "Suggested", counts.SUGGESTED || 0)}
        {chip("confirmed", "Confirmed", (counts.CONFIRMED || 0) + (counts.LOCKED || 0))}
        {chip("all", "All", entries.length)}
      </div>
      {filtered.length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid="entries-empty">Nothing here. Start the timer or add an entry.</p> :
        <div className="border border-[rgba(180,139,63,0.18)]">
          {shown.map((e) => (
            <div key={e.time_entry_id} className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[rgba(180,139,63,0.1)] first:border-t-0 flex-wrap" data-testid={`entry-${e.time_entry_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-display uppercase tracking-wide2 text-[10px] px-2 py-0.5 border ${stateColor(e.entry_state)}`} data-testid={`entry-state-${e.time_entry_id}`}>{e.entry_state}</span>
                  {(e.duration_status === "UNKNOWN" || e.duration_minutes == null)
                    ? <span className="font-serif2 text-lg italic text-[#e7a877]" data-testid={`entry-unknown-${e.time_entry_id}`}>Time not measured</span>
                    : <span className="text-[var(--bone)] font-serif2 text-lg">{fmtHM(e.duration_minutes)}</span>}
                  <span className="text-xs text-[var(--muted)]">{e.entity_dimension || "ALLOCATED"} · {e.activity_category}</span>
                </div>
                <p className="text-xs text-[var(--muted)] mt-1">{fmtDate(e.started_at)} → {fmtDate(e.ended_at)} · {e.source_type}{e.is_backfill ? " · BACKFILL" : ""}{e.idle_adjustment_minutes ? ` · idle −${e.idle_adjustment_minutes}m` : ""}</p>
                {e.description && <p className="text-sm text-[var(--muted)] mt-1 truncate max-w-lg">{e.description}</p>}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {(e.entry_state === "DRAFT" || e.entry_state === "SUGGESTED") && <button className="border border-[#6fae8f] px-3 py-1.5 text-[10px] uppercase tracking-wide2 text-[#8fcbac]" onClick={() => act(timeConfirmEntry, e.time_entry_id, {})} data-testid={`entry-confirm-${e.time_entry_id}`}>Confirm</button>}
                {e.entry_state === "CONFIRMED" && <button className="border border-[rgba(180,139,63,0.4)] px-3 py-1.5 text-[10px] uppercase tracking-wide2 text-[var(--gold-lite)]" onClick={() => act(timeLockEntry, e.time_entry_id)} data-testid={`entry-lock-${e.time_entry_id}`}>Lock</button>}
                {["DRAFT", "SUGGESTED", "CONFIRMED"].includes(e.entry_state) && <button className="border border-[rgba(217,99,59,0.5)] px-3 py-1.5 text-[10px] uppercase tracking-wide2 text-[#e88f6a]" onClick={() => act(timeVoidEntry, e.time_entry_id, "founder_void")} data-testid={`entry-void-${e.time_entry_id}`}>Void</button>}
              </div>
            </div>
          ))}
          {filtered.length > shown.length && (
            <button className="w-full px-4 py-3 border-t border-[rgba(180,139,63,0.1)] text-xs uppercase tracking-wide2 text-[var(--gold-lite)]" onClick={() => setLimit((l) => l + 50)} data-testid="entries-show-more">Show more ({filtered.length - shown.length} remaining)</button>
          )}
        </div>}
    </div>
  );
}

function ActivityTimeline({ refreshKey }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData(await timeActivityTimeline(100)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!data) return <div className="mt-2 text-sm text-[var(--muted)]" data-testid="activity-loading">Loading activity…</div>;
  const c = data.counts;
  const unknownCount = data.founder_touchpoints.filter((s) => s.derived_entry && (s.derived_entry.duration_status === "UNKNOWN" || s.derived_entry.duration_minutes == null)).length;
  const kindTone = (k) => ({ APPROVAL: "border-[#6fae8f] text-[#8fcbac]", REJECTION: "border-[#d98b5f] text-[#e88f6a]", INSTRUCTION: "border-[rgba(180,139,63,0.5)] text-[var(--gold-lite)]", REVIEW_ACK: "border-[rgba(180,139,63,0.4)] text-[var(--gold-lite)]", DECISION: "border-[#6fae8f] text-[#8fcbac]" }[k] || "border-[rgba(180,139,63,0.3)] text-[var(--muted)]");
  const entryTone = (s) => ({ CONFIRMED: "text-[#8fcbac]", LOCKED: "text-[var(--muted)]", SUGGESTED: "text-[#e7a877]", SUPERSEDED: "text-[var(--muted)]", VOIDED: "text-[var(--muted)]" }[s] || "text-[var(--muted)]");
  return (
    <div className="mt-2" data-testid="activity-timeline">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="px-3 py-1.5 text-xs uppercase tracking-wide2 border border-[var(--gold)] text-[var(--gold-lite)]" data-testid="activity-count-touchpoints">Founder touchpoints · {c.founder_touchpoints}</span>
        <span className="px-3 py-1.5 text-xs uppercase tracking-wide2 border border-[rgba(180,139,63,0.25)] text-[var(--muted)]" data-testid="activity-count-ecosystem">Ecosystem activity · {c.ecosystem_activity}</span>
        <span className="px-3 py-1.5 text-xs uppercase tracking-wide2 border border-[rgba(180,139,63,0.25)] text-[var(--muted)]" data-testid="activity-count-runtime">Automated runtime · {c.automated_runtime}</span>
        {unknownCount > 0 && <span className="px-3 py-1.5 text-xs uppercase tracking-wide2 border border-[#d98b5f] text-[#e7a877]" data-testid="activity-needs-duration">Needs duration evidence · {unknownCount}</span>}
      </div>
      {data.founder_touchpoints.length === 0 ? (
        <p className="text-sm text-[var(--muted)]" data-testid="activity-empty">No Founder touchpoints received yet. Inbound signals from Growth will appear here as they arrive.</p>
      ) : (
        <div className="border border-[rgba(180,139,63,0.18)]">
          {data.founder_touchpoints.map((s) => (
            <div key={s.event_id} className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[rgba(180,139,63,0.1)] first:border-t-0 flex-wrap" data-testid={`touchpoint-${s.event_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-display uppercase tracking-wide2 text-[10px] px-2 py-0.5 border ${kindTone(s.founder_interaction_kind)}`} data-testid={`touchpoint-kind-${s.event_id}`}>{s.founder_interaction_kind}</span>
                  <span className="text-xs text-[var(--muted)]">{s.entity_dimension} · {s.source_system || "ecosystem"}</span>
                  {s.signal_state === "SUPERSEDED" && <span className="text-[10px] uppercase tracking-wide2 text-[var(--muted)]">superseded</span>}
                </div>
                <p className="text-xs text-[var(--muted)] mt-1">{fmtDate(s.occurred_at)}{s.evidence_class ? ` · ${s.evidence_class}` : ""}</p>
              </div>
              {s.derived_entry && (
                <span className={`text-[10px] uppercase tracking-wide2 ${entryTone(s.derived_entry.entry_state)}`} data-testid={`touchpoint-entry-${s.event_id}`}>
                  {s.derived_entry.entry_state} · {(s.derived_entry.duration_status === "UNKNOWN" || s.derived_entry.duration_minutes == null) ? "Time not measured" : fmtHM(s.derived_entry.duration_minutes)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-[10px] uppercase tracking-wide2 text-[var(--muted)]" data-testid="activity-note">{data.note}</p>
    </div>
  );
}

function WeeklyDigest({ refreshKey }) {
  const tz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } })();
  const [weekOf, setWeekOf] = useState(null);
  const [d, setD] = useState(null);
  const load = useCallback(async (wk) => {
    try { setD(await timeWeeklyDigest(wk || undefined, tz)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [tz]);
  useEffect(() => { load(weekOf); }, [load, weekOf, refreshKey]);
  if (!d) return <div className="mt-2 text-sm text-[var(--muted)]" data-testid="digest-loading">Loading weekly digest…</div>;
  const t = d.totals;
  const maxDay = Math.max(1, ...d.daily.map((x) => x.minutes));
  const entRows = Object.entries(d.by_entity_dimension || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const projRows = Object.entries(d.by_project || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  return (
    <div className="mt-2" data-testid="weekly-digest">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 text-xs uppercase tracking-wide2 border border-[rgba(180,139,63,0.3)] text-[var(--muted)] hover:text-[var(--bone)]" data-testid="digest-prev" onClick={() => setWeekOf(d.prev_week_start)}>← Prev</button>
          <span className="text-xs text-[var(--bone)]" data-testid="digest-week-label">{d.week_label}</span>
          <button className="px-3 py-1.5 text-xs uppercase tracking-wide2 border border-[rgba(180,139,63,0.3)] text-[var(--muted)] hover:text-[var(--bone)]" data-testid="digest-next" onClick={() => setWeekOf(d.next_week_start)}>Next →</button>
          {weekOf && <button className="text-[10px] uppercase tracking-wide2 text-[var(--gold-lite)]" data-testid="digest-this-week" onClick={() => setWeekOf(null)}>This week</button>}
        </div>
        <span className="text-[10px] uppercase tracking-wide2 text-[var(--muted)]">{d.timezone}</span>
      </div>

      {d.status === "NO_CONFIRMED_TIME" ? (
        <p className="text-sm text-[var(--muted)] border border-[rgba(180,139,63,0.18)] px-4 py-3" data-testid="digest-empty">{d.message}</p>
      ) : (
        <div data-testid="digest-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Total (confirmed+locked)" value={fmtHM(t.total_minutes)} testid="digest-total" />
            <Metric label="Confirmed" value={fmtHM(t.confirmed_minutes)} testid="digest-confirmed" />
            <Metric label="Locked" value={fmtHM(t.locked_minutes)} testid="digest-locked" />
            <Metric label="Entries counted" value={String(d.counted_entries)} testid="digest-count" />
          </div>

          {d.comparison && (
            <p className="mt-3 text-sm" data-testid="digest-comparison">
              vs prior week ({d.comparison.prior_week_start}): {fmtHM(d.comparison.prior_total_minutes)} ·{" "}
              <span className={d.comparison.delta_minutes >= 0 ? "text-[#8fcbac]" : "text-[#e88f6a]"}>
                {d.comparison.delta_minutes >= 0 ? "+" : "−"}{fmtHM(Math.abs(d.comparison.delta_minutes))}
              </span>
            </p>
          )}

          <div className="mt-4" data-testid="digest-daily">
            <p className="font-display uppercase tracking-wide2 text-[10px] text-[var(--gold)]/70 mb-2">Daily distribution</p>
            <div className="flex items-end gap-2 h-24">
              {d.daily.map((day) => (
                <div key={day.date} className="flex-1 flex flex-col items-center justify-end" data-testid={`digest-day-${day.date}`}>
                  <div className="w-full bg-[rgba(180,139,63,0.35)]" style={{ height: `${Math.round((day.minutes / maxDay) * 100)}%` }} title={fmtHM(day.minutes)} />
                  <span className="mt-1 text-[10px] text-[var(--muted)]">{day.weekday}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Breakdown title="By ecosystem entity" obj={d.by_entity_dimension} testid="digest-by-entity" />
            <Breakdown title="By project / workstream" obj={d.by_project} testid="digest-by-project" />
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-4 flex-wrap text-xs">
        <span className={d.entries_awaiting_confirmation ? "text-[var(--gold-lite)]" : "text-[var(--muted)]"} data-testid="digest-awaiting">{d.entries_awaiting_confirmation} awaiting confirmation</span>
        <span className={d.entries_requiring_duration_evidence ? "text-[#e7a877]" : "text-[var(--muted)]"} data-testid="digest-needs-duration">{d.entries_requiring_duration_evidence} need duration evidence</span>
      </div>
      <p className="mt-3 text-[10px] uppercase tracking-wide2 text-[var(--muted)]" data-testid="digest-note">{d.note}</p>
    </div>
  );
}

function Console({ onSignOut }) {
  const [key, setKey] = useState(0);
  const bump = () => setKey((k) => k + 1);
  const genSuggestions = async () => { try { const r = await timeGenerateSuggestions(168); toast.success(`${r.created} suggestion(s) created from activity.`); bump(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="min-h-screen glow-navy px-5 md:px-10 py-10">
      <Seo title="JMH — Founder Time Ledger" />
      <div className="max-w-7xl 2xl:max-w-[1680px] mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Jennae Michelle Holdings · Founder</p>
            <h1 className="mt-1 font-serif2 text-4xl md:text-5xl text-[var(--bone)]">Time & Contribution Ledger</h1>
            <p className="mt-2 text-sm text-[var(--muted)]">Your personal working time — confirmed by you, never inferred from automation.</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/founder/close" className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="link-close">Close Console →</a>
            <button onClick={onSignOut} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="time-signout">Sign out</button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TimerWidget onChange={bump} />
          <QuickEntry onCreated={bump} />
        </div>

        <section className="mt-10" data-testid="dashboard-section">
          <p className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-3">Dashboard</p>
          <Dashboard refreshKey={key} />
        </section>

        <section className="mt-10" data-testid="weekly-digest-section">
          <p className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-1">Weekly Time Digest</p>
          <p className="text-xs text-[var(--muted)] mb-3">Read-only weekly summary of your CONFIRMED and LOCKED time (Preview, in-app only — no external delivery). Suggested and unknown-duration time never count.</p>
          <WeeklyDigest refreshKey={key} />
        </section>

        <section className="mt-10" data-testid="activity-section">
          <p className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-1">Founder Activity Timeline</p>
          <p className="text-xs text-[var(--muted)] mb-3">Inbound Founder touchpoints from the ecosystem (read-only). A touchpoint may create a SUGGESTED entry you still confirm — it is never counted until you do.</p>
          <ActivityTimeline refreshKey={key} />
        </section>

        <section className="mt-10" data-testid="entries-section">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <p className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80">Entries</p>
            <button onClick={genSuggestions} className="border border-[rgba(180,139,63,0.4)] px-3 py-1.5 text-xs uppercase tracking-wide2 text-[var(--gold-lite)]" data-testid="gen-suggestions">Generate activity suggestions</button>
          </div>
          <EntriesList refreshKey={key} onChanged={bump} />
        </section>

        <p className="mt-10 text-[11px] uppercase tracking-wide2 text-[var(--muted)]" data-testid="time-provenance">Preview · synthetic only · no payroll, no journals, no money moved · financial treatment stays UNDETERMINED</p>
      </div>
    </div>
  );
}

export default function FounderTimeLedger() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);
  if (authed === null) return <div className="min-h-screen glow-navy" />;
  if (!authed) return <LoginView onAuthed={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
